import { Trans, useLingui } from "@lingui/react/macro";
import type { BotAvatarSet, BotTemplate } from "@rakazo/contracts";
import { BOT_NAME_MAX_LENGTH } from "@rakazo/contracts";
import {
  NEW_BOT_COLORS,
  newClientId,
  ORGANIC_AVATAR_HEART_FAMILY,
  ORGANIC_AVATAR_LOOKS,
  organicAvatarFamily,
  organicAvatarSampleIdentity,
} from "@rakazo/core";
import {
  BotAvatar,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
  parseBotAvatar,
} from "@rakazo/ui-web";
import { useEffect, useId, useRef, useState } from "react";
import { getNewBotAvatarSet } from "../../lib/new-bot-avatar-set-pref";
import { errorText } from "../../lib/user-error";
import { BotLookPicker } from "./avatar-studio-popover";

/** One preview identity per organic look, so the dialog can show a body before the bot has an id. */
const ORGANIC_IDENTITIES = ORGANIC_AVATAR_LOOKS.map(organicAvatarSampleIdentity);

function pick<T>(options: readonly T[]): T {
  return options[Math.floor(Math.random() * options.length)]!;
}

export interface CreateBotDialogInput {
  name: string;
  color: string;
  /** Reserve the logo preview's persistent seed through creation and retries. */
  id?: string;
  avatarSet: BotAvatarSet;
  /** The organic family the preview drew; omitted when a studio shape is picked. */
  avatarFamily?: number;
  /** The template the bot starts from; omitted for a blank bot. */
  templateId?: string;
}

/** Mounted only while open; the parent unmounts it from `onCancel` or after `onCreate`. */
export function CreateBotDialog({
  onCancel,
  onCreate,
  loadTemplates,
}: {
  onCancel: () => void;
  onCreate: (input: CreateBotDialogInput) => Promise<void>;
  /** The Space's saved and built-in templates for "Start from"; without it every bot is blank. */
  loadTemplates?: () => Promise<BotTemplate[]>;
}) {
  const { t } = useLingui();
  const startFromId = useId();
  const [templates, setTemplates] = useState<BotTemplate[]>([]);
  const [templateId, setTemplateId] = useState("");
  // Loaded once per opening; the dialog mounts only while open.
  const load = useRef(loadTemplates);
  useEffect(() => {
    let live = true;
    load
      .current?.()
      .then((list) => {
        if (live) setTemplates(list);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  const saved = templates.filter((template) => !template.builtin);
  const builtin = templates.filter((template) => template.builtin);
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [previewId] = useState(() => {
    let id: string;
    do {
      id = `c${newClientId().replaceAll("-", "").slice(0, 24)}`;
    } while (organicAvatarFamily(id) === ORGANIC_AVATAR_HEART_FAMILY);
    return id;
  });
  const [look, setLook] = useState(() => ({
    value: pick(NEW_BOT_COLORS),
    identity: pick(ORGANIC_IDENTITIES),
  }));
  const [avatarSet, setAvatarSet] = useState<BotAvatarSet>(getNewBotAvatarSet);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();

  async function create() {
    if (!trimmed || submitting) return;
    setError(null);
    setSubmitting(true);
    const organic = avatarSet === "organic" && parseBotAvatar(look.value).shapeIndex === undefined;
    try {
      await onCreate({
        name: trimmed,
        color: look.value,
        ...(avatarSet === "rakazo" ? { id: previewId } : {}),
        avatarSet,
        ...(organic ? { avatarFamily: organicAvatarFamily(look.identity) } : {}),
        ...(templateId ? { templateId } : {}),
      });
    } catch (err) {
      setError(errorText(err, t`Could not create Nexa Mate`));
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !submitting) onCancel();
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        initialFocus={nameRef}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-md"
        data-testid="create-bot-dialog"
      >
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-center">
              <Trans>New Nexa Mate</Trans>
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <BotAvatar
              color={look.value}
              identity={avatarSet === "rakazo" ? previewId : look.identity}
              size={96}
              avatarSet={avatarSet}
            />
          </div>
          <Input
            ref={nameRef}
            aria-label={t`Name`}
            placeholder={t`Name your Nexa Mate`}
            value={name}
            maxLength={BOT_NAME_MAX_LENGTH}
            onChange={(event) => setName(event.target.value)}
            className="text-center"
          />
          {templates.length ? (
            <div className="space-y-1.5">
              <label htmlFor={startFromId} className="text-[13px] text-muted-foreground">
                <Trans>Start from</Trans>
              </label>
              <NativeSelect
                id={startFromId}
                className="w-full"
                value={templateId}
                data-testid="create-bot-start-from"
                onChange={(event) => {
                  const next = templates.find((template) => template.id === event.target.value);
                  const previous = templates.find((template) => template.id === templateId);
                  // A name the person typed stays; one filled from the last pick follows the new one.
                  if (!trimmed || trimmed === previous?.name) setName(next?.name ?? "");
                  setTemplateId(event.target.value);
                }}
              >
                <NativeSelectOption value="">{t`Blank`}</NativeSelectOption>
                {saved.length ? (
                  <NativeSelectOptGroup label={t`Saved`}>
                    {saved.map((template) => (
                      <NativeSelectOption key={template.id} value={template.id}>
                        {template.name}
                      </NativeSelectOption>
                    ))}
                  </NativeSelectOptGroup>
                ) : null}
                <NativeSelectOptGroup label={t`Built-in`}>
                  {builtin.map((template) => (
                    <NativeSelectOption key={template.id} value={template.id}>
                      {template.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelectOptGroup>
              </NativeSelect>
            </div>
          ) : null}
          {error ? (
            <p role="alert" data-testid="create-bot-error" className="text-[13px] text-destructive">
              {error}
            </p>
          ) : null}
          <BotLookPicker
            value={look.value}
            identity={avatarSet === "rakazo" ? previewId : look.identity}
            organicIdentities={ORGANIC_IDENTITIES}
            avatarSet={avatarSet}
            onAvatarSetChange={(set, value) => {
              setAvatarSet(set);
              setLook((current) => ({ ...current, value }));
            }}
            onChange={(value, identity) => {
              setLook((current) => ({ value, identity: identity ?? current.identity }));
            }}
          />
          <DialogFooter>
            <Button type="button" variant="outline" disabled={submitting} onClick={onCancel}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" disabled={!trimmed || submitting}>
              <Trans>Create</Trans>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
